
import { appColors } from "@/common/AppConst";
import { CssColorType, ElAlertType } from "@/common/customTypes";
import { COUNT_NO_LIMIT, User } from "@/common/entity/User";
import { RootStore } from "@/vuex-store";
import {
  defineComponent,
  onMounted,
  PropType,
  ref,
  watch,
  WatchStopHandle,
} from "@vue/composition-api";
import { onActivated } from "@vue/runtime-dom";
import {
  StayOnPageReason,
  ToolTopTips,
  ToolType,
  useHeihuTool,
} from "iframe-tools-sdk";

/** 顶部警告提示信息 */
export type TopAlert = { title: string; type: ElAlertType };

/** `IframeTools` 组件属性 */
export type IframeToolProps = {
  /** 工具类型 */
  toolType: ToolType;
  /** 离开页面时，对应不应离开原因的提示信息 */
  stayReasonTip: { [key in StayOnPageReason]: string } | string;
  /**
   * 是否禁用功能，`boolean` 值表示禁用或不禁用，字符串值表示：
   * - user：需要登录
   * - number类型: 至少拥有多少个纠错次数或以上
   */
  disabled?: boolean | "user" | number;
  /** `disabled` 属性为 `true` 时的对应提示给用户的信息 */
  disabledTopAlert?: TopAlert;
  /** 顶部说明信息 */
  topTipProps?: ToolTopTips;
};

/** iframe占位文本信息 */
export type IframePlaceholder = {
  type: CssColorType;
  text: string;
  isLoading?: boolean;
};

/** 离开页面时，对应不应离开原因的提示信息 */
export type StayOnPageReasonTip =
  | { [key in StayOnPageReason]: string }
  | string;

export default defineComponent({
  /** 类型同步 {@link IframeToolProps} */
  props: {
    /**
     * 工具类型
     */
    toolType: {
      type: String as PropType<IframeToolProps["toolType"]>,
      required: true,
    },
    /**
     * 离开页面时，对应不应离开原因的提示信息
     */
    stayReasonTip: {
      type: [Object, String] as PropType<IframeToolProps["stayReasonTip"]>,
      required: true,
    },
    /**
     * 是否禁用功能，`boolean` 值表示禁用或不禁用，字符串值表示：
     * - user：需要登录
     * - vip：需要是普通会员或超级会员
     * - svip: 需要是超级会员
     */
    disabled: [Boolean, String, Number] as PropType<
      IframeToolProps["disabled"]
    >,
    /**
     * `disabled` 属性为 `true` 时的对应提示给用户的信息
     */
    disabledTopAlert: Object as PropType<IframeToolProps["disabledTopAlert"]>,
    /**
     * 顶部说明信息
     */
    topTipProps: Object as PropType<IframeToolProps["topTipProps"]>,
  },
  setup(props) {
    /** 加载中iframe占位文本信息 */
    const loadingPlaceholder: IframePlaceholder = {
      type: "info",
      text: "加载中",
      isLoading: true,
    };

    /** 调用 `useHeihuTool` 获取可以与 iframe 互动的方法 */
    const {
      start,
      enable,
      disable,
      onConnected,
      onStayOnPageReasonChange,
      getStayOnPageReason,
    } = useHeihuTool("hhw2sqfvyl39khn7xmc8ar0bupjdilgo", {
      /** 配置 iframe 内部的主题颜色 */
      theme: {
        /** 主题主要颜色 */
        sourceColor: appColors.primaryColor,
      },
    });

    /** @see SetupResult.topAlert  */
    const topAlert = ref<TopAlert>();
    /** @see SetupResult.iframePlaceholder  */
    const iframePlaceholder = ref<IframePlaceholder>();

    /**
     * 根据用户信息状态和禁用值获取对应的提示信息
     * @param user 用户信息，null 表示未登录
     * @param disabled 禁用值
     * @param disabledAlert 禁用值为 `true` 时的提示信息
     * @returns 对应提示信息
     */
    function getAlert(
      user: User | null,
      disabled: IframeToolProps["disabled"],
      disabledAlert: IframeToolProps["disabledTopAlert"],
    ): TopAlert | undefined {
      // props.disabled --> false true user number
      // 不禁用，不提示信息
      if (!disabled) return undefined;
      // props.disabled --> true user number
      // `props.disabled` 值为 `true`，返回 `props.disabledTopAlert`
      if (disabled === true) return disabledAlert;
      // props.disabled --> user number
      // 未登录，提示需要登录
      if (!user) return { title: "需要登录", type: "error" };
      // 对于登录用户，`props.disabled` 为 user 时
      if (disabled === "user") return undefined;
      // props.disabled --> number
      // 检查纠错次数是否足够
      if (user.correctCount !== COUNT_NO_LIMIT && user.correctCount < disabled)
        return {
          title: `使用该功能需要至少剩余AI功能次数${disabled}次，不包含试用次数，使用时不消耗次数`,
          type: "error",
        };
      return undefined;
    }
    /** 更新时修改工具禁用状态和顶部提示信息 */
    watch(
      () => ({
        user: RootStore.state.currUser,
        disabled: props.disabled,
        alert: props.disabledTopAlert,
      }),
      ({ user, disabled, alert }) => {
        console.log(
          "-------------watch user=",
          user,
          " disabled=",
          disabled,
          " alert=",
          alert,
        );
        topAlert.value = getAlert(user, disabled, alert);
        if (topAlert.value) disable();
        else enable();
      },
      { immediate: true, deep: true },
    );

    /** @see SetupResult.iframe */
    const iframe = ref<HTMLIFrameElement>();

    // 取消将听 `toolType` 的变化
    let cancelWatchType: WatchStopHandle | null = null;

    /** @see SetupResult.stayReason */
    const stayReason = ref<StayOnPageReason | null>(getStayOnPageReason());

    onMounted(() => {
      // 连接 iframe 网页成功（页面显示成功）后取消 iframe 占位文本
      onConnected(() => {
        iframePlaceholder.value = undefined;
        stayReason.value = null;
      });
      // `toolType` 变化时，重新加载对应的工具
      if (cancelWatchType) cancelWatchType();
      cancelWatchType = watch(
        () => props.toolType,
        (toolType: ToolType, oldToolType: ToolType | undefined) => {
          if (toolType === oldToolType) return;
          stayReason.value = null;
          // 修改 iframe 占位字符状态
          iframePlaceholder.value = loadingPlaceholder;
          /** 调用相应方法进行相应操作，这里表示开始在 iframe 中加载“视频转音频”工具页面 */
          start(iframe.value!, toolType, { topTips: props.topTipProps });
        },
        { immediate: true },
      );
    });

    /** 不应离开页面的原因发生变化时修改 `stayReason` 的值 */
    onStayOnPageReasonChange((reason: StayOnPageReason | null) => {
      stayReason.value = reason;
    });

    /** 导航回当前页面时 */
    onActivated(() => {
      stayReason.value = null;
      // 修改 iframe 占位字符状态
      iframePlaceholder.value = loadingPlaceholder;
    });

    return {
      topAlert,
      iframePlaceholder,
      iframe,
      stayReason,
    };
  },

  beforeRouteLeave(to, from, next) {
    // 原因为空，表示不需要提醒，让用户直接离开
    if (!this.stayReason) {
      next();
      return;
    }
    // 确定提示信息
    const message =
      typeof this.stayReasonTip === "string"
        ? this.stayReasonTip
        : this.stayReasonTip[this.stayReason];
    // 不为空，根据原因提醒用户
    this.$confirm(message, "警告", {
      type: "warning",
    })
      .then(() => next())
      .catch(() => next(false));
  },
});
